/**
 * OIDC login against the A3RN identity provider.
 *
 * The user-visible shape of this is the main consequence of the migration
 * (identity-architecture §6): instead of the biometric prompt firing in-app, the app opens a
 * system browser sheet, the hosted login handles email and passkey, and a deep
 * link returns here. On iOS the first run also shows a system consent dialog
 * naming auth.a3rn.com, which cannot be suppressed.
 *
 * Two things about the flow are load-bearing and easy to get wrong:
 *
 * 1. **The authorization code must be captured before any navigation guard
 *    runs.** The root layout redirects unauthenticated users to /(auth)/login,
 *    and on 2026-08-02 that ate a real code: the IdP redirected correctly, the
 *    SPA booted, the guard fired, and the code was gone from the address bar
 *    before anything read it. app/auth/callback.tsx is exempted for exactly
 *    this reason.
 * 2. **The verifier must survive the round trip.** On web the browser leaves the
 *    page entirely, so an in-memory verifier is lost and every login fails with
 *    invalid_grant. It is persisted, and cleared on use.
 */

import * as AuthSession from 'expo-auth-session';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { decodeJwtPayload } from '../utils/jwt';
import { getOidcConfig, getRedirectUri, OIDC_SCOPES } from './oidcConfig';

/** Survives a full page navigation on web; single-use. */
const PKCE_KEY = '@cynosure/oidc_pkce';

export interface OidcTokens {
  accessToken: string;
  refreshToken: string | null;
  idToken: string;
  /**
   * The route the flow that started this login asked to be sent back to, if it
   * asked for one. Carried here rather than in a second storage key so it
   * shares the PKCE record's single-use lifecycle — one write, one read, one
   * clear — instead of needing its own cleanup that an abandoned login skips.
   */
  returnTo?: string;
}

/**
 * What a refresh returns.
 *
 * ⚠️  `idToken` IS OPTIONAL, NOT ABSENT, and this file used to say the opposite:
 *     "a refresh does not issue one". It does. ZITADEL mints a fresh ID token on
 *     the refresh grant whenever the session's scope contains `openid`, which
 *     ours always does (`internal/api/oidc/token.go`,
 *     `accessTokenResponseFromSession`). Believing otherwise is what left the
 *     stored ID token to age out — 12h — while the session lived on through
 *     refresh, so by logout time the only `id_token_hint` we had was expired,
 *     `isUsableIdTokenHint` dropped it, and ZITADEL fell back to its Login-v2
 *     interstitial instead of redirecting to the app.
 *
 * Still a separate type rather than `Partial<OidcTokens>`: `idToken` is optional
 * here and required there, so the compiler keeps a caller from writing an
 * absent one over a stored one.
 */
export interface RefreshedTokens {
  accessToken: string;
  refreshToken: string | null;
  idToken?: string;
}

export class OidcError extends Error {}

interface PendingLogin {
  verifier: string;
  state: string;
  returnTo?: string;
}

async function discovery(issuer: string): Promise<AuthSession.DiscoveryDocument> {
  // Discovered rather than hardcoded, so pointing an app at Okta or Entra stays
  // a config change (identity-architecture §1). expo-auth-session caches this per issuer.
  const found = await AuthSession.fetchDiscoveryAsync(issuer);
  if (!found?.authorizationEndpoint || !found?.tokenEndpoint) {
    throw new OidcError('Identity provider discovery is missing required endpoints');
  }
  return found;
}

/**
 * Build the authorization request and hand the user to the hosted login.
 *
 * Returns the code on native, where the browser sheet resolves in-process. On
 * web it never returns: the page navigates away and app/auth/callback.tsx picks
 * the flow back up.
 */
export async function startLogin(
  options: { prompt?: 'login'; returnTo?: string } = {},
): Promise<{ code: string } | null> {
  const config = getOidcConfig();
  if (!config) throw new OidcError('Identity provider login is not configured');

  const found = await discovery(config.issuer);
  const redirectUri = getRedirectUri();

  const request = new AuthSession.AuthRequest({
    clientId: config.clientId,
    redirectUri,
    scopes: OIDC_SCOPES,
    responseType: AuthSession.ResponseType.Code,
    usePKCE: true,
    // `prompt=login` forces a REAL re-authentication rather than reusing the
    // existing IdP session, and advances `auth_time` with it (measured across
    // three consecutive logins in Phase 1). That advance is the entire point:
    // it is what a step-up needs and what an ordinary login cannot supply,
    // because ZITADEL ignores `acr_values` (identity-architecture §4).
    ...(options.prompt ? { extraParams: { prompt: options.prompt } } : {}),
  });

  // Generates the verifier/challenge pair as a side effect, so it must run
  // before the verifier is persisted.
  const authUrl = await request.makeAuthUrlAsync(found);

  const pending: PendingLogin = {
    verifier: request.codeVerifier ?? '',
    state: request.state,
    // Rides with the verifier for the same reason the verifier is here at all:
    // on web the browser leaves the page entirely, so anything the return leg
    // needs has to be on disk before the redirect. Without it app/auth/callback
    // always lands on '/' and the root layout re-decides from scratch — which
    // is how a step-up for account reset used to dump the user on /restore
    // instead of back on the screen that asked for it.
    ...(options.returnTo ? { returnTo: options.returnTo } : {}),
  };
  if (!pending.verifier) throw new OidcError('PKCE verifier was not generated');
  await AsyncStorage.setItem(PKCE_KEY, JSON.stringify(pending));

  // ⚠️  WEB NAVIGATES THIS TAB. It must not use `promptAsync`, which opens a
  //     POPUP (expo-web-browser's web implementation is `window.open` at
  //     500x650) and then waits for that popup to hand the code back via
  //     `maybeCompleteAuthSession`. The handoff cannot happen here: the popup
  //     returns from the identity provider with `window.opener` severed —
  //     measured null on 2026-08-04, against a real login — so nothing can post
  //     the message and `promptAsync` never resolves.
  //
  //     What happened instead is worth stating, because it looked like several
  //     unrelated bugs. The popup fell through to /auth/callback, completed the
  //     login itself and BECAME the app: Cynosure ran in a 500px window while
  //     the tab the user started from sat on /login with no way to sign out or
  //     switch user. Production Faro sessions carry
  //     browser_viewportWidth=500 / browser_viewportHeight=649, which is that
  //     popup rather than anyone's device. Worse, `stepUp()` waits on the
  //     promise `promptAsync` returns, so account reset hung forever on
  //     "Resetting…" — the user re-authenticated in a window whose result
  //     nothing was listening for.
  //
  //     A full-page redirect is also what the rest of this file already
  //     assumes: the PKCE verifier is persisted to AsyncStorage precisely
  //     because "the browser leaves the page entirely", and both callers
  //     document a null return on web as the page having navigated away.
  //     Only this call disagreed.
  if (Platform.OS === 'web') {
    window.location.assign(authUrl);
    // Nothing after this runs — app/auth/callback.tsx resumes the flow when the
    // provider sends the browser back.
    return null;
  }

  const result = await request.promptAsync(found);

  if (result.type === 'success' && result.params.code) {
    return { code: result.params.code };
  }
  if (result.type === 'error') {
    throw new OidcError(result.params.error_description || result.params.error || 'Login failed');
  }
  // 'dismiss' / 'cancel' — the user backed out, which is not an error, and on
  // web this is also the path taken when the page simply navigated away.
  return null;
}

/**
 * The sign-in this browser started, checked against `state`, consumed once.
 *
 * WEB uses this instead of `exchangeCode`: the code and verifier go to the API
 * (`webSession.signIn`), which exchanges them and keeps the refresh token out of
 * script's reach (identity-architecture §9).
 */
export async function takePendingLogin(
  state?: string,
): Promise<{ verifier: string; returnTo?: string }> {
  const raw = await AsyncStorage.getItem(PKCE_KEY);
  // Single-use whatever happens, as in `exchangeCode`.
  await AsyncStorage.removeItem(PKCE_KEY);
  if (!raw) {
    throw new OidcError('No login in progress — the sign-in attempt may have expired');
  }
  const pending: PendingLogin = JSON.parse(raw);
  if (state && pending.state && state !== pending.state) {
    throw new OidcError('Login response did not match the request that started it');
  }
  return {
    verifier: pending.verifier,
    ...(pending.returnTo ? { returnTo: pending.returnTo } : {}),
  };
}

/**
 * Exchange an authorization code for tokens. NATIVE only — see takePendingLogin.
 *
 * `state` is compared against the value stored when the request was built. A
 * mismatch means the response did not come from the request we started, which
 * is the CSRF case the parameter exists for.
 */
export async function exchangeCode(code: string, state?: string): Promise<OidcTokens> {
  const config = getOidcConfig();
  if (!config) throw new OidcError('Identity provider login is not configured');

  const raw = await AsyncStorage.getItem(PKCE_KEY);
  if (!raw) {
    throw new OidcError('No login in progress — the sign-in attempt may have expired');
  }
  const pending: PendingLogin = JSON.parse(raw);

  if (state && pending.state && state !== pending.state) {
    throw new OidcError('Login response did not match the request that started it');
  }

  const found = await discovery(config.issuer);

  let tokens: AuthSession.TokenResponse;
  try {
    tokens = await AuthSession.exchangeCodeAsync(
      {
        clientId: config.clientId,
        code,
        redirectUri: getRedirectUri(),
        extraParams: { code_verifier: pending.verifier },
      },
      found,
    );
  } finally {
    // Single-use whatever happens. Leaving a spent verifier behind makes the
    // NEXT login fail confusingly rather than this one fail clearly.
    await AsyncStorage.removeItem(PKCE_KEY);
  }

  if (!tokens.idToken) {
    // Provisioning needs the profile claims, and only the ID token carries
    // them — the access token holds no email at all (identity-architecture §2).
    throw new OidcError('Identity provider returned no id_token');
  }

  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken ?? null,
    idToken: tokens.idToken,
    // Read from the record above, which the `finally` has already deleted — so
    // this is consumed exactly once, like the verifier beside it.
    ...(pending.returnTo ? { returnTo: pending.returnTo } : {}),
  };
}

/**
 * Exchange a refresh token for a fresh access token.
 *
 * ⚠️  IDP SESSIONS COULD NOT REFRESH AT ALL BEFORE THIS EXISTED, and the way it
 *     hid is worth keeping. The API client's 401 handler posted to Cynosure's
 *     own `/auth/refresh`, which hashed the token and looked it up in the
 *     `refresh_tokens` table. An IdP refresh token was never in that table, so
 *     the lookup missed, the endpoint answered 401, and the handler concluded
 *     the session was dead and logged the user out.
 *
 *     Nothing errored. It looked exactly like a normal expiry, so the symptom
 *     was "signed out again" every time an access token aged out — which is
 *     easy to blame on the identity provider's token lifetimes rather than on
 *     there being no refresh path at all.
 *
 * Returns null when the refresh token is absent or the provider rejects it.
 * That is the genuine "session is over" answer, and the caller logs out on it —
 * so it must NOT be conflated with a network failure, which leaves the session
 * intact and retries later.
 */
export async function refreshTokens(refreshToken: string): Promise<RefreshedTokens | null> {
  const config = getOidcConfig();
  if (!config) throw new OidcError('Identity provider login is not configured');

  const found = await discovery(config.issuer);

  const tokens = await AuthSession.refreshAsync(
    { clientId: config.clientId, refreshToken },
    found,
  );

  if (!tokens.accessToken) return null;

  return {
    accessToken: tokens.accessToken,
    // ZITADEL rotates refresh tokens, so the new one replaces the old — but it
    // only returns one when rotation is on. Falling back to the token we just
    // used keeps a non-rotating configuration working instead of silently
    // dropping the ability to refresh again.
    refreshToken: tokens.refreshToken ?? refreshToken,
    // Carried forward, NOT dropped — see RefreshedTokens. This is the only
    // thing that keeps the id_token_hint alive across a long session, and the
    // hint is what makes logout redirect back to the app instead of stopping at
    // the provider's session picker. Left undefined if a provider returns none;
    // `authStore.setTokens` then preserves the stored one.
    idToken: tokens.idToken ?? undefined,
  };
}

/** Whether a spent or abandoned login attempt is still on disk. */
export async function clearPendingLogin(): Promise<void> {
  await AsyncStorage.removeItem(PKCE_KEY);
}

/**
 * End the session at the IDENTITY PROVIDER, not just in this app.
 *
 * ⚠️  WITHOUT THIS, LOGGING OUT DOES NOT LOG YOU OUT. Clearing Cynosure's
 *     tokens leaves the IdP's own session cookie alive, so the next press of
 *     "Continue with A3RN" hits /authorize, ZITADEL sees a live session and
 *     returns a code immediately — signing the user straight back in **with no
 *     passkey prompt**. On a shared or borrowed device that means the next
 *     person to open the app is signed in as whoever used it last.
 *
 *     This was live on 2026-08-02: the clients had `postLogoutRedirectUris`
 *     registered from the start and nothing ever called this endpoint, so half
 *     the logout contract existed and the half that ends the session did not.
 *
 * `id_token_hint` is what lets the IdP end the right session without asking the
 * user to confirm. Without it ZITADEL hands the browser to its Login-v2
 * interstitial (`TerminateSessionFromRequest` takes that branch on
 * `IDTokenHintClaims == nil`), which is a session picker that does not redirect
 * anywhere by itself — the user is simply left on auth.a3rn.com.
 *
 * `logout_hint` is the safety net for that branch: it lets the interstitial pick
 * the session and follow the redirect without a click. See `logoutHintFrom`.
 *
 * `post_logout_redirect_uri` must byte-match one registered on the client
 * (a3rn-fleet configure.py), or ZITADEL ends the session and then refuses to
 * send the user back.
 */
/**
 * Whether an ID token is OURS, and so fit to send as `id_token_hint`.
 *
 * ⚠️  DO NOT REINSTATE AN EXPIRY CHECK HERE. This used to reject any token past
 *     its `exp`, on the reasoning that the provider would refuse it. ZITADEL
 *     does the opposite, deliberately and in writing — `zitadel/oidc`
 *     `pkg/op/verifier_id_token_hint.go`:
 *
 *       "In case of an expired token both the Claims and first encountered
 *        expiry related error is returned … the caller can choose to still
 *        trust the token for cases like logout, as signature and other
 *        verifications succeeded."
 *
 *     and `pkg/op/session.go` takes exactly that choice, on the end-session
 *     path: `if err != nil && !errors.As(err, &IDTokenHintExpiredError{})`. So
 *     an expired-but-genuine hint still names the session and still ends it in
 *     one hop. Rejecting it is what sent every session older than the ID
 *     token's lifetime to the Login-v2 session picker instead of back here.
 *
 * ⚠️  A HINT THE PROVIDER REFUSES IS STILL WORSE THAN NO HINT AT ALL — local
 *     state is already cleared by the time the browser navigates to
 *     end_session, so a 4xx there leaves the user on an error page while the
 *     session logout exists to end is still alive. That is the risk this
 *     function exists to manage; expiry was simply the wrong instrument.
 *
 * What it was actually protecting against — tokens left over from a previous
 * ZITADEL install, observed 2026-08-04 as a 400 for a malformed hint and a 404
 * for a stale-epoch one — is a signature or issuer failure, which expiry never
 * detected anyway. `iss` is the half of that checkable without the key set. A
 * token minted by a previous install at the SAME issuer URL still gets through:
 * accepted deliberately, because a reinstall forces every user to
 * re-authenticate regardless, whereas the expiry gate broke sign-out daily.
 * Closing that last gap means checking `kid` against the live JWKS.
 *
 * Unparseable counts as not ours.
 */
function isOurIdToken(idToken: string | null, issuer: string): idToken is string {
  if (!idToken) return false;
  return decodeJwtPayload(idToken)?.iss === issuer;
}

/**
 * The login name to hand ZITADEL as `logout_hint`, from an ID token that may
 * already be expired.
 *
 * This is the FALLBACK for the case `isOurIdToken` rejects. Without an
 * `id_token_hint` ZITADEL cannot name a session to end, so it hands the browser
 * to its Login-v2 interstitial — a session picker the user has to click, and
 * which never redirects back to the app on its own. Given `logout_hint` that
 * page matches it against each session's `loginName`, clears the one that
 * matches and follows `post_logout_redirect_uri` with no interaction at all.
 *
 * ⚠️  `preferred_username`, NOT `email`. The page compares against ZITADEL's
 *     `PreferredLoginName` and `preferred_username` is that same value
 *     (`internal/api/oidc/userinfo.go`). They are equal for our users today,
 *     which is exactly why picking the wrong one would go unnoticed until an
 *     org sets a login-name policy that separates them.
 *
 * Deliberately applies NONE of `isOurIdToken`'s conditions: a token this app
 * would not send as `id_token_hint` still names the login the user signed in
 * with, and that is the whole point of reading it here. A name that matches no
 * session costs a warning on the picker; not sending one costs the redirect.
 */
/**
 * The A3RN session this client belongs to — the ID token's `sid` — sent to the
 * API as `X-IdP-Session` (identity-architecture §6). The access token carries
 * no `sid`, so this is the only way the API can tell the client that its
 * session was ended elsewhere: ZITADEL keeps refresh tokens alive after the
 * session ends, and back-channel logout is how Cynosure hears of it.
 */
export function sessionIdFrom(idToken: string | null): string | null {
  if (!idToken) return null;
  const sid = decodeJwtPayload(idToken)?.sid;
  return typeof sid === 'string' && sid ? sid : null;
}

/**
 * Revoke a refresh token at the IdP. Best-effort: the caller is signing out
 * regardless, and a failure only leaves a token this device has forgotten.
 *
 * Needed because ending a session does NOT revoke its refresh tokens in
 * ZITADEL (measured on v4.19.2: a refresh after end_session still succeeds).
 */
export async function revokeRefreshToken(refreshToken: string | null): Promise<void> {
  const config = getOidcConfig();
  if (!config || !refreshToken) return;
  try {
    const found = await discovery(config.issuer);
    await AuthSession.revokeAsync(
      {
        clientId: config.clientId,
        token: refreshToken,
        tokenTypeHint: AuthSession.TokenTypeHint.RefreshToken,
      },
      found,
    );
  } catch {
    // See above: nothing to recover.
  }
}

function logoutHintFrom(idToken: string | null): string | null {
  if (!idToken) return null;
  const name = decodeJwtPayload(idToken)?.preferred_username;
  return typeof name === 'string' && name ? name : null;
}

/**
 * Whether the browser has already been handed to the IdP's end-session
 * endpoint. Web only, and deliberately never reset.
 *
 * ⚠️  THE SECOND CALL IS ALWAYS THE WORSE ONE. Whatever starts a duplicate
 *     sign-out does so after the store has been emptied, so it arrives with
 *     `idToken` null — no `id_token_hint`, no `logout_hint` — and its
 *     `location.assign` supersedes the first one's. ZITADEL then cannot name a
 *     session to end and shows its Login-v2 picker, which redirects nowhere on
 *     its own. `authStore` has its own single-flight guard; this one holds even
 *     if a future caller reaches this function directly.
 *
 *     Not reset by this function, because on web nothing after
 *     `location.assign` matters: the page is leaving. Native is excluded
 *     entirely — `openAuthSessionAsync` genuinely finishes and the app must
 *     stay able to sign out again.
 *
 *     One exception, handled below: iOS Safari's back-forward cache can bring
 *     this page back — module state and all — when the user taps Back from the
 *     IdP. Then "the page is leaving" was false, and with the flag still set
 *     Sign Out would do nothing at all. A restored page starts over.
 */
let handedToEndSession = false;

if (
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  typeof window.addEventListener === 'function'
) {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) handedToEndSession = false;
  });
}

export async function endIdpSession(idToken: string | null): Promise<void> {
  if (Platform.OS === 'web' && handedToEndSession) return;

  const config = getOidcConfig();
  if (!config) return;

  let endpoint: string | undefined;
  try {
    const found = await discovery(config.issuer);
    endpoint = found.endSessionEndpoint ?? undefined;
  } catch {
    // Discovery is best-effort here. Local state is already cleared by the
    // time this runs, so a failure must not strand the user in a half
    // logged-out app — it just leaves the IdP session to expire on its own.
    return;
  }
  if (!endpoint) return;

  const params = new URLSearchParams({ client_id: config.clientId });
  if (isOurIdToken(idToken, config.issuer)) params.set('id_token_hint', idToken);

  // Sent alongside the hint, not instead of it. With a usable `id_token_hint`
  // ZITADEL terminates the session itself and never looks at this; without one
  // it is what stops the Login-v2 interstitial from becoming a dead end.
  const hint = logoutHintFrom(idToken);
  if (hint) params.set('logout_hint', hint);

  if (Platform.OS === 'web') {
    params.set('post_logout_redirect_uri', `${window.location.origin}/`);
    handedToEndSession = true;
    window.location.assign(`${endpoint}?${params.toString()}`);
    return;
  }

  params.set('post_logout_redirect_uri', 'cynosure://auth/logout');
  await WebBrowser.openAuthSessionAsync(
    `${endpoint}?${params.toString()}`,
    'cynosure://auth/logout',
  );
}
